// Install / Retry for one app on one PC (F-CAT-09, ADR-0056; spec/13 §3.3, §9.1): asks the PC to install the app as soon as it
// is free, never during a session, as a one-off (Install for players stays as it is). Shared by the station window's Games and
// apps list and the library entry's per-PC list. `library:manage` is the caller's gate; the button itself is hidden without it.

import { useTranslation } from '@clavian/i18n/react';
import { Button, useToast } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { useCurrentVenue } from '../../shell/scope';
import { useInstallStationApp } from './queries';
import { useProblem } from '../../api/use-problem';

export function InstallAppButton({
  stationId,
  entryId,
  name,
  action,
}: {
  readonly stationId: string;
  readonly entryId: string;
  /** The app's name, for the toast and the button's accessible name. */
  readonly name: string;
  readonly action: 'install' | 'retry';
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const install = useInstallStationApp(venue.organizationId, venue.id);
  const ask = () =>
    install.mutate(
      { stationId, entryId },
      {
        onSuccess: () => show({ tone: 'ok', title: t('floor.drawer.installed.installSent', { app: name }) }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('floor.drawer.installed.installFailed', { app: name }) }) }),
      },
    );
  return (
    <Button
      variant="ghost"
      size="sm"
      busy={install.isPending}
      aria-label={t(action === 'retry' ? 'floor.drawer.installed.retryLabel' : 'floor.drawer.installed.installLabel', { app: name })}
      onClick={ask}
    >
      {t(action === 'retry' ? 'floor.drawer.installed.retry' : 'floor.drawer.installed.install')}
    </Button>
  );
}
